// src/components/admin/TicketsContent.jsx
import React, { useState, useEffect, useMemo } from "react";
// import axios from "axios";
import { toast } from "react-toastify";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import Pagination from "./Pagination";
import { useTickets } from "./hooks/useTickets";
import CustomDropdown from "./components/extras/CustomDropdown";
import Loader from '../Loader.jsx';
// import host from "../../global.jsx";
// import Cookies from "js-cookie";

const TicketsContent = () => {
  //   const convaiadmin_accessToken = Cookies.get("accessToken");

  // Ticket-related states
  //   const [TicketsRaised, setTicketsRaised] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentTicketPage, setCurrentTicketPage] = useState(1);
  //   const [expandedTickets, setExpandedTickets] = useState();
  const [expandedRow, setExpandedRow] = useState(new Set());
  const [remarks, setRemarks] = useState({});
  const [resolvedChecks, setResolvedChecks] = useState({});

  const ticketsPerPage = 10;

  const { tickets, loading, updateTicket } = useTickets();

  // Reset expanded rows when filter changes
  useEffect(() => {
    setExpandedRow(new Set());
  }, [statusFilter]);

  // Toggle row expand (only one row open at a time)
  const toggleRowExpand = (globalIndex) => {
    setExpandedRow((prev) => {
      if (prev.has(globalIndex)) {
        return new Set();
      } else {
        return new Set([globalIndex]);
      }
    });
  };

  const handleUpdate = async (ticketId, remark, globalIndex, resolved) => {
    // We can use the passed values directly or fallback to state if needed.
    // The previous code was using state: const remark = remarks[ticketId]
    // But the onClick passes the current value. Let's use the passed values for consistency 
    // with the arguments, but fall back to state if they are somehow missing.

    // Note: The onClick call passes: ticket._id, remarks[ticket._id] || "", globalIndex, resolvedChecks[ticket._id] || false
    // So arguments match: (ticketId, remark, globalIndex, resolved)

    const success = await updateTicket(ticketId, remark, resolved);
    if (success) {
      setExpandedRow(new Set());
      setRemarks(prev => { const n = { ...prev }; delete n[ticketId]; return n; });
      setResolvedChecks(prev => { const n = { ...prev }; delete n[ticketId]; return n; });
    }
  };

  // Filtered, Sorted & Paginated Tickets
  const {
    sortedFilteredTickets,
    paginatedTickets,
    totalTicketPages,
    totalTicketItems,
  } = useMemo(() => {
    if (!tickets)
      return {
        sortedFilteredTickets: [],
        paginatedTickets: [],
        totalTicketPages: 0,
        totalTicketItems: 0,
      };

    // Filter by status
    const filtered =
      statusFilter === "All"
        ? tickets
        : tickets.filter((ticket) => {
          const status = (ticket.status || ticket.Status || "Pending").toLowerCase();
          const lowerFilter = statusFilter.toLowerCase();
          if (lowerFilter === "submitted")
            return status.includes("pending") || status === "submitted";
          if (lowerFilter === "in progress")
            return status.includes("progress");
          if (lowerFilter === "resolved")
            return status.includes("resolved") || status.includes("done");
          return false;
        });

    // Sort newest first
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdDate || 0).getTime();
      const dateB = new Date(b.createdDate || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / ticketsPerPage);
    const start = (currentTicketPage - 1) * ticketsPerPage;
    const end = start + ticketsPerPage;
    const paginated = sorted.slice(start, end);

    return {
      sortedFilteredTickets: sorted,
      paginatedTickets: paginated,
      totalTicketPages: totalPages,
      totalTicketItems: totalItems,
    };
  }, [tickets, statusFilter, currentTicketPage]);

  if (loading) {
    return <div className="text-white text-center py-10"><Loader/></div>;
  }

  return (
    <div className="glass-effect rounded-xl p-8 mb-8 border border-white/10 backdrop-blur-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-white">Raised Tickets</h2>
        <div className="w-48">
          <CustomDropdown
            options={[
              { value: "All", label: "All Status" },
              { value: "Submitted", label: "Pending" },
              { value: "In Progress", label: "In Progress" },
              { value: "Resolved", label: "Resolved" },
            ]}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setCurrentTicketPage(1);
            }}
            placeholder="Filter by Status"
            icon={<SlidersHorizontal className="w-4 h-4" />}
          />
        </div>
      </div>

      {totalTicketItems > 0 ? (
        <>
          <div className="overflow-x-auto scrollbar-thin">
            <style jsx>{`
              .scrollbar-thin::-webkit-scrollbar {
                width: 6px;
                height: 6px;
              }
              .scrollbar-thin::-webkit-scrollbar-track {
                background: rgba(255, 255, 255, 0.1);
                border-radius: 3px;
              }
              .scrollbar-thin::-webkit-scrollbar-thumb {
                background: rgba(255, 255, 255, 0.3);
                border-radius: 3px;
              }
              .scrollbar-thin::-webkit-scrollbar-thumb:hover {
                background: rgba(255, 255, 255, 0.5);
              }
            `}</style>

            <table className="w-full border-t border-b border-[#5A4479D4] table-fixed">
              <thead>
                <tr className="border-t border-b border-[#5A4479D4]">
                  <th className="text-left text-xs text-[#D8D8D8] font-medium py-4 px-12 w-1/3">Title</th>
                  <th className="text-left text-xs text-[#D8D8D8] font-medium py-4 px-6 w-24">Raised By</th>
                  <th className="text-left text-xs text-[#D8D8D8] font-medium py-4 px-6 w-32">Date</th>
                  <th className="text-left text-xs text-[#D8D8D8] font-medium py-4 px-[6%] w-24">Status</th>
                  <th className="text-left text-xs text-[#D8D8D8] font-medium py-4 px-6 w-20">Details</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTickets.map((ticket, index) => {
                  const globalIndex = (currentTicketPage - 1) * ticketsPerPage + index;
                  const isExpanded = expandedRow.has(globalIndex);

                  return (
                    <React.Fragment key={ticket._id}>
                      <tr
                        className="border-b border-gray-700/30 hover:bg-gray-700/20 cursor-pointer transition-colors"
                        onClick={() => toggleRowExpand(globalIndex)}
                      >
                        <td className="py-4 px-12 text-[#CCCCCCE5] text-sm w-1/3 truncate">
                          {ticket.title || "NA"}
                        </td>
                        <td className="py-4 px-6 text-[#CCCCCCE5] text-sm w-24 truncate">
                          {ticket.orgadminName || "NA"}
                        </td>
                        <td className="py-4 px-6 text-[#CCCCCCE5] text-sm w-32 truncate">
                          {ticket.createdDate
                            ? new Date(ticket.createdDate).toISOString().split("T")[0]
                            : "NA"}
                        </td>
                        <td className="py-4 px-6 text-[#CCCCCCE5] text-sm w-24">
                          <div
                            className={`text-sm font-medium px-3 py-1 rounded-full flex justify-center ${["pending", "submitted"].includes(
                              (ticket.status || ticket.Status || "").toLowerCase()
                            )
                                ? "bg-red-500/20 text-red-400"
                                : ["resolved", "done"].includes(
                                  (ticket.status || ticket.Status || "").toLowerCase()
                                )
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-yellow-500/20 text-yellow-400"
                              }`}
                          >
                            {(() => {
                              const raw = (ticket.Status || ticket.status || "Pending").toLowerCase();
                              if (["pending", "submitted"].includes(raw)) return "Pending";
                              if (["resolved", "done"].includes(raw)) return "Resolved";
                              if (raw.includes("progress")) return "In Progress";
                              return raw.replace(/^./, (c) => c.toUpperCase());
                            })()}
                          </div>
                        </td>
                        <td className="py-4 px-6 text-[#CCCCCCE5] text-sm w-20">
                          <button onClick={(e) => { e.stopPropagation(); toggleRowExpand(globalIndex); }}>
                            <ChevronDown
                              className={`h-5 w-5 text-[#CCCCCCE5] transition-transform duration-300 ${isExpanded ? "rotate-180" : ""
                                }`}
                            />
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan="5" className="p-0">
                            <div
                              className="transition-all duration-500 ease-in-out overflow-hidden"
                              style={{
                                background: "linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))",
                                backdropFilter: "blur(40px)",
                              }}
                            >
                              <div className="pl-12">
                                <div className="grid md:grid-cols-2 gap-8">
                                  <div className="space-y-3 pt-4">
                                    <p><strong className="text-[#D8D8D8] text-sm">Email:</strong> <span className="text-[#CCCCCCE5] text-sm">{ticket.orgadminEmail || "NA"}</span></p>
                                    <p><strong className="text-[#D8D8D8] text-sm">Date:</strong> <span className="text-[#CCCCCCE5] text-sm">
                                      {ticket.createdDate ? new Date(ticket.createdDate).toISOString().split("T")[0] : "NA"}
                                    </span></p>
                                    <p><strong className="text-[#D8D8D8] text-sm">Title:</strong> <span className="text-[#CCCCCCE5] text-sm">{ticket.title || "NA"}</span></p>
                                    <p><strong className="text-[#D8D8D8] text-sm">Description:</strong> <span className="text-[#CCCCCCE5] text-sm">{ticket.description || "NA"}</span></p>
                                  </div>
                                  <div className="space-y-4 p-5 backdrop-blur-md bg-white/5">
                                    <div className="space-y-3 max-h-32 overflow-y-auto pr-2">
                                      {ticket.remarks && ticket.remarks.length > 0 ? (
                                        [...ticket.remarks].reverse().map((remark, i) => {
                                          const d = new Date(remark.timestamp);
                                          const formatted = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                                          return (
                                            <div key={i} className="flex justify-start">
                                              <div className="max-w-xs px-4 py-2 rounded-lg text-sm bg-gray-700 text-[#CCCCCCE5]">
                                                <p className="font-medium text-xs opacity-80 mb-1">{formatted}</p>
                                                <p className="break-words">{remark.text}</p>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <p className="text-gray-500 text-sm italic">No remarks yet.</p>
                                      )}
                                    </div>

                                    <div>
                                      <label className="block text-[#D8D8D8] text-sm mb-1">
                                        <span>Add Remark<span className="text-red-800">*</span></span>
                                      </label>
                                      <textarea
                                        value={remarks[ticket._id] || ""}
                                        onChange={(e) =>
                                          setRemarks((prev) => ({
                                            ...prev,
                                            [ticket._id]: e.target.value,
                                          }))
                                        }
                                        className="w-full h-10 px-3 py-2 bg-transparent border border-gray-700/50 rounded text-[#CCCCCCE5] placeholder-gray-400 resize-none text-sm focus:border-blue-500 focus:outline-none transition-colors"
                                        rows={4}
                                        placeholder="Type your remark here..."
                                      />
                                    </div>

                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`resolved-${ticket._id}`}
                                          checked={resolvedChecks[ticket._id] || false}
                                          onChange={(e) =>
                                            setResolvedChecks((prev) => ({
                                              ...prev,
                                              [ticket._id]: e.target.checked,
                                            }))
                                          }
                                          className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-500 rounded focus:ring-blue-500"
                                        />
                                        <label htmlFor={`resolved-${ticket._id}`} className="text-[#D8D8D8] text-sm">
                                          Resolved
                                        </label>
                                      </div>
                                      <button
                                        onClick={() =>
                                          handleUpdate(
                                            ticket._id,
                                            remarks[ticket._id] || "",
                                            globalIndex,
                                            resolvedChecks[ticket._id] || false
                                          )
                                        }
                                        className="px-8 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        Update
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentTicketPage}
            totalPages={totalTicketPages}
            onPageChange={setCurrentTicketPage}
            totalItems={totalTicketItems}
            itemsPerPage={ticketsPerPage}
          />
        </>
      ) : (
        <p className="text-[#CCCCCCE5] text-sm text-center py-8">
          No tickets found for the selected status.
        </p>
      )}
    </div>
  );
};

export default TicketsContent;